import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Card, CardContent } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import { ArrowLeft, ChevronLeft, ChevronRight, Info, ShieldCheck, RefreshCw } from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const Route = createFileRoute('/_admin/lotep/estruturas/$structureId/pool')({
  component: LotepPoolNumbersPage,
});

interface PoolNumberStatus {
  number: number;
  status: 'open' | 'drawn';
  prize_won: boolean;
  prize_tier: number;
  prize_value_brl: string;
  prize_catalog_id: string;
  prize_catalog_name: string;
  prize_catalog_image_url: string;
  drawn_at: string;
  game_id: string;
  game_title: string;
  game_modality_code: string;
  winner_name_masked: string;
  ticket_id: string;
}

interface PoolNumbersResponse {
  pool_id: string;
  page: number;
  page_size: number;
  numbers_total: number;
  drawn_total: number;
  numbers: PoolNumberStatus[];
  prizes_total: number;
  prizes_awarded: number;
  prizes_remaining: number;
}

interface LotepStructure {
  id: string;
  lotep_code: string;
  modality_slug: string;
  total_numbers: number;
  status: string;
}

const PAGE_SIZE = 200;

function LotepPoolNumbersPage() {
  const { structureId } = Route.useParams();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'drawn' | 'open'>('all');
  const [selected, setSelected] = useState<PoolNumberStatus | null>(null);

  // Fetch structure metadata
  const { data: structure } = useQuery<LotepStructure>({
    queryKey: ['admin-lotep-structure', structureId],
    queryFn: async () => {
      const r = await api.get(`/admin/lotep/structures/${structureId}`);
      return r.data?.structure as LotepStructure;
    },
  });

  // Pool ID vem via search param (passado pelo botao "Pool" na lista de estruturas)
  const url = new URL(window.location.href);
  const poolIdParam = url.searchParams.get('pool_id') ?? '';
  const [poolId, setPoolId] = useState(poolIdParam);

  const { data, isLoading, refetch, isFetching } = useQuery<PoolNumbersResponse>({
    queryKey: ['admin-lotep-pool-numbers', poolId, page, filter],
    enabled: !!poolId,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      params.set('filter', filter);
      const r = await api.get(`/pool/lotep/pools/${poolId}/numbers?${params}`);
      return r.data as PoolNumbersResponse;
    },
    refetchInterval: 5000, // polling
  });

  const totalPages = data ? Math.ceil(data.numbers_total / PAGE_SIZE) : 1;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={ShieldCheck}
        title="Pool LOTEP — Números"
        description={structure ? `${structure.lotep_code} · ${structure.modality_slug}` : 'Carregando...'}
        actions={
          <div className="flex gap-2">
            <Link to="/lotep/estruturas">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        }
      />

      {!poolId && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              Pool ID não informado. Adicione <code>?pool_id=&lt;uuid&gt;</code> à URL ou clique em "Visualizar Pool" na lista de estruturas.
            </p>
            <input
              className="mt-3 w-full border rounded px-2 py-1 text-sm bg-background"
              placeholder="Cole o pool_id (UUID) aqui"
              value={poolId}
              onChange={e => setPoolId(e.target.value.trim())}
            />
          </CardContent>
        </Card>
      )}

      {poolId && (
        <>
          <div className="flex gap-3 items-center flex-wrap">
            <Select value={filter} onValueChange={v => { setFilter(v as 'all' | 'drawn' | 'open'); setPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="drawn">Sorteados</SelectItem>
                <SelectItem value="open">Disponíveis</SelectItem>
              </SelectContent>
            </Select>

            {data && (
              <div className="text-sm text-muted-foreground">
                <Badge variant="secondary">{data.drawn_total.toLocaleString('pt-BR')}</Badge> de <Badge variant="outline">{data.numbers_total.toLocaleString('pt-BR')}</Badge> sorteados ({((data.drawn_total / data.numbers_total) * 100).toFixed(1)}%)
              </div>
            )}
          </div>

          {/* Acompanhamento de prêmios: total colocado vs sorteado vs restante */}
          {data && (
            <Card>
              <CardContent className="p-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Prêmios na pool</p>
                  <p className="text-2xl font-bold">{data.prizes_total.toLocaleString('pt-BR')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sorteados</p>
                  <p className="text-2xl font-bold text-emerald-600">{data.prizes_awarded.toLocaleString('pt-BR')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Restantes</p>
                  <p className="text-2xl font-bold text-amber-500">{data.prizes_remaining.toLocaleString('pt-BR')}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4">
              {isLoading && <p className="text-sm text-muted-foreground">Carregando números...</p>}
              {data && (
                <div className="grid grid-cols-10 md:grid-cols-15 lg:grid-cols-20 gap-1.5">
                  {data.numbers.map(n => (
                    <button
                      key={n.number}
                      type="button"
                      onClick={() => setSelected(n)}
                      className={`relative aspect-square rounded text-xs font-mono flex items-center justify-center transition-colors ${
                        n.status === 'drawn'
                          ? n.prize_won
                            ? 'bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-600'
                            : 'bg-zinc-600/40 hover:bg-zinc-600/60 border border-zinc-500'
                          : 'bg-background hover:bg-muted border border-muted'
                      }`}
                      title={`#${n.number} — ${n.status}${n.prize_won ? ' · 🏆' : ''}`}
                    >
                      <span>{n.number}</span>
                      {n.status === 'drawn' && (
                        <Info className="absolute top-0.5 right-0.5 h-2.5 w-2.5 text-foreground/60" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Página {page} de {totalPages} · números {((page - 1) * PAGE_SIZE + 1).toLocaleString('pt-BR')}–{Math.min(page * PAGE_SIZE, data?.numbers_total ?? 0).toLocaleString('pt-BR')}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal info do numero */}
      <Dialog open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono">Número #{selected?.number}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant={selected.status === 'drawn' ? 'default' : 'outline'}>
                  {selected.status === 'drawn' ? 'Sorteado' : 'Disponível'}
                </Badge>
                {selected.prize_won && <Badge className="bg-emerald-600">Premiado</Badge>}
              </div>

              {selected.status === 'drawn' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Sorteado em</p>
                      <p className="font-medium">
                        {selected.drawn_at
                          ? format(new Date(selected.drawn_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Modalidade</p>
                      <p className="font-medium">{selected.game_modality_code || '—'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Jogo</p>
                    <p className="font-medium">{selected.game_title || '—'}</p>
                    {selected.game_id && (
                      <Link to="/jogos" className="text-xs text-primary hover:underline">
                        ver jogos →
                      </Link>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Ganhador (mascarado)</p>
                    <p className="font-medium">{selected.winner_name_masked || '—'}</p>
                  </div>

                  {selected.prize_won && (
                    <div className="rounded-md border border-emerald-600/30 bg-emerald-600/5 p-3">
                      <p className="text-xs text-muted-foreground mb-1">Prêmio</p>
                      <div className="flex items-center gap-3">
                        {selected.prize_catalog_image_url && (
                          <img
                            src={selected.prize_catalog_image_url}
                            alt={selected.prize_catalog_name}
                            className="h-12 w-12 rounded object-cover"
                            loading="lazy"
                          />
                        )}
                        <div>
                          <p className="font-medium">
                            {selected.prize_catalog_name || `Tier ${selected.prize_tier}`}
                          </p>
                          <p className="text-emerald-600 font-mono">R$ {selected.prize_value_brl}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selected.ticket_id && (
                    <div>
                      <p className="text-xs text-muted-foreground">Ticket ID</p>
                      <p className="font-mono text-xs break-all">{selected.ticket_id}</p>
                    </div>
                  )}
                </>
              )}

              {selected.status === 'open' && (
                <p className="text-muted-foreground italic">Número ainda não foi sorteado.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
